import cx from 'clsx';
import React from 'react';
import { useDropzone } from 'react-dropzone';
import { v4 as uuidv4 } from 'uuid';

import { ReactComponent as PhotoIcon } from '@assets/svg/ic-photo.svg';

import Button from '@components/uikit/Button/Button';

import { FILE_SIZE_LIMIT } from '@constants/dropzoneFileLimits';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import { DEFAULT_ACCEPT_FILES } from '../../constants/defaultAcceptFiles';
import FileItem from '../FileItem/FileItem';
import style from './style.module.scss';

const Dropzone = ({
  maxFilesCount = 500,
  typeOfFiles,
  currentFiles,
  type = '',
  onDrop,
  variant = 'zone',
  maxFileSize = FILE_SIZE_LIMIT,
  className = '',
}) => {
  const { t } = useTranslation();

  const acceptTypesOfFiles = typeOfFiles || DEFAULT_ACCEPT_FILES;

  const isDisabledDropzone = currentFiles.length === maxFilesCount;

  const { getRootProps, getInputProps, open } = useDropzone({
    accept: acceptTypesOfFiles,
    disabled: isDisabledDropzone,
    onDrop: (acceptedFiles) => {
      let files;

      files = acceptedFiles.filter(
        (file) =>
          !currentFiles.find(
            (cf) => cf.name === file.name && cf.size === file.size && cf.lastModified === file.lastModified,
          ),
      );

      if (files.length < acceptedFiles.length) {
        showToast({ variant: 'error', message: t('Duplicate files are not allowed') });
      }

      files = [
        ...currentFiles,
        ...files.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          }),
        ),
      ];

      if (files.length > maxFilesCount) {
        showToast({ variant: 'error', message: t('Maximum number of files exceeded.') });
        files = files.slice(files.length - maxFilesCount);
      }

      onDrop(files);
    },
    validator: (file) =>
      file.size > maxFileSize ? showToast({ variant: 'error', message: t('The file is too large.') }) : null,
    noClick: true,
    noKeyboard: true,
  });

  const removeFile = (fileName) => {
    const filesArr = currentFiles.filter((item) => item.name !== fileName);

    onDrop(filesArr);
  };

  if (variant === 'photoBtn') {
    return (
      <div className={cx(style.photoBtn, className)} onClick={open}>
        <PhotoIcon />
        <span className={style.photoBtnText}>{t('Add photo')}</span>
        <input {...getInputProps()} />
      </div>
    );
  }

  const acceptList = Object.values(acceptTypesOfFiles).flat(1).join(', ').replace(/\./g, '');

  return (
    <div
      {...getRootProps()}
      className={cx(style.dropzoneBlock, { [style.emptyBlock]: !currentFiles.length })}
    >
      <div
        className={cx(style.dropzoneBlockContainer, {
          [style.disabledDropzoneBlockContainer]: isDisabledDropzone,
        })}
      >
        <div className={cx(style.btnWrapper, { [style.btnInlineWrapper]: !!currentFiles.length })}>
          <p className={cx(style.dropZoneBlockTitle, { [style.dropZoneInlineTitle]: !!currentFiles.length })}>
            {type === 'equipmentImageAttach' ? t('Drag & drop photo here') : t('Drag & drop files here')}
            <span>{t('or')}</span>
          </p>
          <Button
            type={currentFiles.length ? 'secondary' : 'outline'}
            text={t('Upload from computer')}
            onClick={open}
            disabled={isDisabledDropzone}
          />
        </div>
        <div className={style.supportTypesBlock}>{`${t('Supports')} ${acceptList} ${t('files')}`}</div>
      </div>
      <input {...getInputProps()} />
      {!!currentFiles.length && (
        <div className={style.dropzoneBlockPreview}>
          {currentFiles.map((file) => (
            <FileItem key={uuidv4()} file={file} removeFunc={() => removeFile(file.name)} />
          ))}
        </div>
      )}
    </div>
  );
};

export default Dropzone;
